import { Dispatch, MouseEvent, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Container, Tree } from 'rsuite'
import { TreeNodeProps, TreeTopProps, TTreeNodeItem } from '@client/stores/tabsStore'
import { ConverterListIcon, ExportListIcon, RunMappingIcon } from '@client/config'
import { Button } from '@client/components/Controls'
import { TContextMenuItem, useLayout } from '@client/components/Layout/LayoutManager'
import ConverterListModel, { TMappingItem } from '@client/models/converter'
import EditConverterList from './EditConverterList'
import Converter from './Converter'
import Exporter from '../exporter/Exporter'
import { Capabilities, hasPermission, hasRole, RoleType } from '@/shared'
import project from '@client/stores/projectStore'
import { TContextMenuCurrentItem } from '@/client/components/ContextMenu'
import ModalDialog from '@/client/components/ModalDialog'
import { useContextMenuTree } from '@/client/utils/useContextMenu'
import { toastError, toastSuccess } from '@client/utils/toastMessage.tsx'

// Refactors already introduced in your codebase:
import useSidebarInvalidation from '@client/utils/useSidebarInvalidation.ts'
import useListContextMenuItems from '@client/utils/useListContextMenuItems.ts'
import { findTreeNodeById } from '@/client/utils/listContextMenuDefaults'

type TSelectedContextMenu = TContextMenuCurrentItem & {
    type: string
}
type ContextMenuProps = {
    contextMenu: TSelectedContextMenu
    setContextMenu: Dispatch<SetStateAction<TSelectedContextMenu | null>>
}
type TRelatedOutTable = {
    _id: string
    name: string
}
type TContextMenuVariables = {
    title: string
    content: ReactNode | string
    buttonLabel: string
}
type TRelatedInTable = {
    _id: string // `${inTableId}-${fileId}`
    inTableId: string
    outTableId: string
    file?: {
        _id?: string
        fileName?: string
        sheetName?: string
        client?: string
    }
}

type TRelatedTablesResponse = {
    converter: {
        _id: string
        name: string
        outTableId: string
    }
    outTable: TRelatedOutTable | null
    inTables: TRelatedInTable[]
}

const converterListModel = new ConverterListModel()

const ContextMenu = ({ contextMenu, setContextMenu }: ContextMenuProps) => {
    const { t } = useTranslation()
    const [related, setRelated] = useState<TRelatedTablesResponse | null>(null)
    const [loadingRelated, setLoadingRelated] = useState(false)
    const [relatedError, setRelatedError] = useState<string | null>(null)
    const [deleteOutTable, setDeleteOutTable] = useState(false)
    const [deleteInTableIds, setDeleteInTableIds] = useState<Set<string>>(new Set())
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => {
        if (!contextMenu || contextMenu.type !== 'delete') return

        const projectId = project.currentProject.value?._id || ''
        if (!projectId || !contextMenu.id) return

        let cancelled = false
        setLoadingRelated(true)
        setRelatedError(null)
        setRelated(null)
        setDeleteOutTable(false)
        setDeleteInTableIds(new Set())

        converterListModel
            .relatedTables(projectId, contextMenu.id)
            .then((result: any) => {
                if (cancelled) return
                const payload = result?.data ?? result
                setRelated(payload || null)
            })
            .catch((err: any) => {
                if (cancelled) return
                setRelatedError(err?.message || 'Failed to load related tables.')
            })
            .finally(() => {
                if (cancelled) return
                setLoadingRelated(false)
            })

        return () => {
            cancelled = true
        }
    }, [contextMenu])

    const vars: TContextMenuVariables = useMemo(() => {
        if (contextMenu.type === 'delete') {
            return {
                // The name goes in as a value, not into the key: concatenated
                // into the key the way it was before, the key changed with
                // every converter, so no translation could ever match it. Same
                // form as the data list and template sidebars use.
                title: t('Delete {{name}}?', { name: contextMenu.label || '' }),
                content: '',
                buttonLabel: t('Delete'),
            }
        }
        return { title: '', content: '', buttonLabel: '' }
    }, [contextMenu, t])

    const content: ReactNode =
        contextMenu.type === 'delete' ? (
            <div>
                <p>
                    {t('Are you sure you want to delete this converter list?')}
                    <br />
                    {t('This action cannot be undone.')}
                </p>

                {loadingRelated && <p>{t('Loading related tables...')}</p>}
                {relatedError && <p style={{ color: 'red' }}>{relatedError}</p>}

                {!loadingRelated && !relatedError && related && (
                    <div className="converter-related-tables">
                        {related.outTable && (
                            <label style={{ display: 'block', marginTop: '0.5em' }}>
                                <input
                                    type="checkbox"
                                    checked={deleteOutTable}
                                    onChange={(e) => setDeleteOutTable(e.target.checked)}
                                />{' '}
                                {t('Delete template')}: <strong>{related.outTable.name}</strong> ({related.outTable._id}
                                )
                            </label>
                        )}

                        {related.inTables && related.inTables.length > 0 && (
                            <div style={{ marginTop: '0.75em' }}>
                                <div>{t('Delete input files')}:</div>
                                {related.inTables.map((it) => {
                                    const checked = deleteInTableIds.has(it._id)
                                    const f = it.file || {}
                                    const baseName = f.fileName || t('file')
                                    const displayName = f.sheetName ? `${baseName} - ${f.sheetName}` : baseName
                                    const client = f.client ? ` (${f.client})` : ''

                                    return (
                                        <label
                                            key={it._id}
                                            style={{ display: 'block', marginLeft: '1em', marginTop: '0.25em' }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    setDeleteInTableIds((prev) => {
                                                        const next = new Set(prev)
                                                        if (e.target.checked) next.add(it._id)
                                                        else next.delete(it._id)
                                                        return next
                                                    })
                                                }}
                                            />{' '}
                                            {displayName}
                                            {client}
                                        </label>
                                    )
                                })}
                            </div>
                        )}

                        {!related.outTable && (!related.inTables || related.inTables.length === 0) && (
                            <p style={{ marginTop: '0.5em' }}>{t('No related tables were found.')}</p>
                        )}
                    </div>
                )}
            </div>
        ) : (
            vars.content
        )

    const buttons = [
        {
            status: contextMenu.type,
            label: vars.buttonLabel,
            disabled: contextMenu.type === 'delete' && (loadingRelated || isDeleting),
        },
        {
            status: 'cancel',
            label: t('Cancel'),
            disabled: isDeleting,
        },
    ]

    function handleDialogButtons(status: string) {
        if (status === 'cancel') {
            if (isDeleting) return false
            setContextMenu(null)
            return true
        }

        if (status === 'delete') {
            if (loadingRelated || isDeleting) return false

            const projectId = project.currentProject.value?._id || ''
            const converterId = contextMenu?.id

            if (projectId && converterId) {
                setIsDeleting(true)

                const payload = {
                    deleteOutTable,
                    deleteInputFiles: Array.from(deleteInTableIds),
                }

                const selectedFilesCount = payload.deleteInputFiles.length
                const willDeleteOutTable = !!(related?.outTable && deleteOutTable)

                converterListModel
                    .delete(projectId, converterId, payload as any)
                    .then(() => {
                        const parts: string[] = [t('Converter deleted successfully.')]

                        if (willDeleteOutTable) parts.push(t('Output table deleted.'))

                        if (selectedFilesCount > 0) {
                            parts.push(
                                selectedFilesCount === 1
                                    ? t('1 input file deleted.')
                                    : t('{{count}} input files deleted.', { count: selectedFilesCount })
                            )
                        }

                        toastSuccess(parts.join(' '))

                        // Keep existing store invalidations (prototype)
                        setTimeout(() => {
                            project.converterLists.value = null
                            if (selectedFilesCount > 0) project.dataLists.value = null
                            if (willDeleteOutTable) project.templates.value = null
                        }, 100)
                    })
                    .catch((err: any) => {
                        console.error('Failed to delete converter:', err)
                        toastError(t('Failed to delete converter.'))
                    })
                    .finally(() => {
                        setIsDeleting(false)
                        setContextMenu(null)
                    })

                return false
            }
        }

        setContextMenu(null)
        return true
    }

    return (
        <ModalDialog
            id="project-list-dialog"
            title={vars.title}
            buttons={buttons}
            onClick={handleDialogButtons}
            size="sm"
        >
            {content}
        </ModalDialog>
    )
}

export default function SidebarConverters() {
    const { t } = useTranslation()
    const { addTab, NoProjectSelected, onContextMenu, openTabToRight, removeTabCopies, removeOtherTabs } = useLayout()

    const { data, loading } = project.useConverterLists()
    const hasProject = Boolean(project.currentProject.value?._id)

    const enableConverter = !!(data && data?.length > 0)
    const enableExporter = !!(data && data?.length > 0)

    const [contextMenu, setContextMenu] = useState<TSelectedContextMenu | null>(null)

    const refetchConverters = useCallback(() => {
        project.converterLists.value = null
    }, [])

    // Invalidation: keep it broad for prototype; tighten once you standardize server table names
    useSidebarInvalidation({
        tables: ['converter', 'converterList', 'mapping', 'outTableColumn', 'template', 'dataList'],
        actions: ['updated', 'deleted'],
        refetch: refetchConverters,
    })

    const treeNodes = useMemo(() => {
        if (data?.length == 0) return []
        const converterLists: any = { label: t('Mappings'), value: 'mappings', children: [] as any[] }
        data?.forEach((item: TMappingItem) => {
            converterLists.children.push({
                id: item._id,
                outTableId: item.outTableId,
                label: item.name,
                value: `${item.outTableId} - ${item.name}`,
                parent: converterLists,
            } as never)
        })
        return [converterLists]
    }, [data, t])

    const converterController = {
        getTabContent(params: any) {
            const { tab } = params
            return <Converter tab={tab} />
        },
        getBreadCrumb(params: any) {
            return [t('Converter')]
        },
    }

    function onOpenConverterList(event: MouseEvent<HTMLElement>): void {
        addTab({
            dataId: `convert-mapping-list`,
            name: t('Converter'),
            component: `convert-mapping-list`,
            controller: converterController,
            icon: <RunMappingIcon />,
            store: { treeNodes: null, editor: null },
        })
    }

    const exportController = {
        getTabContent(params: any) {
            const { tab } = params
            return <Exporter tab={tab} />
        },
        getBreadCrumb(params: any) {
            return [t('Exporter')]
        },
    }

    function onOpenExportList(event: MouseEvent<HTMLElement>): void {
        addTab({
            dataId: `export-mapping-list`,
            name: t('Exporter'),
            component: `export-mapping-list`,
            controller: exportController,
            icon: <ExportListIcon />,
            store: { treeNodes: null, editor: null },
        })
    }

    const mappingController = {
        getTabContent(params: any) {
            const { tab } = params
            return <EditConverterList tabId={tab.id} />
        },
    }

    function newTab(node: TTreeNodeItem) {
        return {
            dataId: `mapping-${node.id}`,
            name: node.label,
            component: node.id,
            controller: mappingController,
            icon: <ConverterListIcon />,
            store: { data: node },
        }
    }

    function onOpenTab(node: TTreeNodeItem) {
        addTab(newTab(node))
    }

    function TreeTop({ data }: TreeTopProps) {
        return (
            <>
                <Container className="tree-top">
                    {hasPermission(Capabilities.convert_write) && (
                        <Button
                            appearance="subtle"
                            onClick={onOpenConverterList}
                            title={t('Open Converter')}
                            disabled={!enableConverter}
                        >
                            <RunMappingIcon />
                        </Button>
                    )}
                    {hasPermission(Capabilities.export_read) && (
                        <Button
                            appearance="subtle"
                            onClick={onOpenExportList}
                            title={t('Open Exports')}
                            disabled={!enableExporter}
                        >
                            <ExportListIcon />
                        </Button>
                    )}
                </Container>
            </>
        )
    }

    function TreeNode({ node }: TreeNodeProps) {
        if (node.children) return node.label
        return node.value
    }

    function EmptyTree() {
        return (
            <div className="empty-tree-list">
                {loading ? (
                    t('Loading ...')
                ) : (
                    <>
                        {t('The list is empty.')}
                        <br />
                        <br />
                        {t('Upload a template to see the mapping list.')}
                    </>
                )}
            </div>
        )
    }

    function onSelectTreeItem(activeNode: any): void {
        const hasChildren = (activeNode?.children?.length || 0) > 0
        if (!hasChildren) onOpenTab(activeNode)
    }

    const getNodeById = useCallback(
        (id: string): TTreeNodeItem | null => {
            return (findTreeNodeById(treeNodes, id) as TTreeNodeItem) || null
        },
        [treeNodes]
    )

    const canDelete = hasRole(RoleType.Administrator) || hasPermission(Capabilities.convert_delete)

    const extraItems = useMemo<TContextMenuItem[]>(() => {
        if (!canDelete) return []
        return [
            {
                label: t('Delete'),
                onClick: (_event, current) =>
                    setContextMenu({
                        ...current,
                        type: 'delete',
                    }),
            },
        ]
    }, [t, canDelete])

    const { show } = useListContextMenuItems<TTreeNodeItem>({
        t,
        onContextMenu,
        addTab,
        openTabToRight,
        removeTabCopies,
        removeOtherTabs,
        getItemById: getNodeById,
        createTab: newTab,
        extraItems,
        includeSeparatorBeforeExtra: true,
    })

    const handleContextMenuCapture = useContextMenuTree(treeNodes, show)

    return !hasProject ? (
        NoProjectSelected()
    ) : (
        <>
            <div className="sidebar-content sidebar-content-converter" onContextMenuCapture={handleContextMenuCapture}>
                <TreeTop data={project.templates} />

                {enableConverter ? (
                    <Tree
                        className="sidebar-content-tree"
                        data={treeNodes}
                        defaultExpandItemValues={['mappings']}
                        renderTreeNode={(node) => <TreeNode node={node} />}
                        onSelect={onSelectTreeItem}
                    />
                ) : (
                    <EmptyTree />
                )}

                {contextMenu && <ContextMenu contextMenu={contextMenu} setContextMenu={setContextMenu} />}
            </div>
        </>
    )
}
